import 'mapbox-gl/dist/mapbox-gl.css';

// eslint-disable-next-line import-length/import-length
import { PriceFromStyled } from '@portalterreno/nextjs-client/portalterreno/components/PropertyCard';
import { BreadcrumbItem } from '@portalterreno/react-ui/Breadcrumb';
import Visible from '@portalterreno/react-ui/Visible';
import { Col, Row } from '@portalterreno/react-ui-core/Grid';
import { useTranslation } from 'next-i18next';
import React, { Fragment, useMemo } from 'react';

import useLegacyProjectContactForm from '../../../hooks/useLegacyProjectContactForm';
import useProperty from '../../../hooks/useProperty';
import slugify from '../../../utils/slugify';
import CampaignSubContent from '../../Campaign/CampaignSubContent';
import ContactForm from '../../ContactForm';
import SellerInformation from '../../SellerInformation';
import StructuredData from '../../StructuredData';
import Breadcrumb from '../Breadcrumb';
import Features from '../Features';
import LocationTabs from '../LocationTabs';
import PropertyPageHeader from '../PropertyPageHeader';
import Badge from '../PropertyPageHeader/Badge';
import RelatedProperties from '../RelatedProperties';
import StickyContainer from '../StickyContainer';
import StyledReadMore from '../StyledReadMore';
import SubContent from '../SubContent';
import BadgesList, { BadgesListProps } from './BadgesList';
import FeatureList from './FeatureList';
import Feature from './FeatureList/Feature';
import FormHeader from './FormHeader';
import FormValues from './FormValues';

export type ProjectPageProps = Pick<BadgesListProps, 'badges'>;

const ProjectPage: React.FC<ProjectPageProps> = ({ badges }) => {
  const { t } = useTranslation('property');
  const property = useProperty();
  const { sendContact, sendShowPhoneContact, sendWhatsappContact } =
    useLegacyProjectContactForm();

  const regionSlug = React.useMemo(
    () => slugify(property.regionName),
    [property.regionName]
  );

  const citySlug = React.useMemo(
    () => slugify(property.cityName),
    [property.cityName]
  );
  const communeSlug = React.useMemo(
    () => slugify(property.communeName),
    [property.communeName]
  );
  const operation = React.useMemo(() => t('sell'), [t]);
  const operationFormatted = React.useMemo(
    () => slugify(operation),
    [operation]
  );
  const translatedPropertyType = useMemo(
    () => t(property.propertyType),
    [t, property.propertyType]
  );

  const region = slugify(t('region'));
  const city = slugify(t('city'));
  const commune = slugify(t('commune'));

  const breadCrumbItems: Array<BreadcrumbItem> = React.useMemo(() => {
    const breadcrumbList = [
      {
        label: process.env.NEXT_PUBLIC_COUNTRY.toUpperCase(),
        link: `${process.env.NEXT_PUBLIC_LEGACY_HOST}/${process.env.NEXT_PUBLIC_COUNTRY}/`
      },
      {
        label: property.regionName,
        link: `${process.env.NEXT_PUBLIC_LEGACY_HOST}/${process.env.NEXT_PUBLIC_COUNTRY}/terreno/venta/${region}/${property.regionId}-${regionSlug}`
      },
      {
        label: property.cityName,
        link: `${process.env.NEXT_PUBLIC_LEGACY_HOST}/${process.env.NEXT_PUBLIC_COUNTRY}/terreno/venta/${city}/${property.cityId}-${citySlug}`
      }
    ];
    if (property.communeId !== 0) {
      breadcrumbList.push({
        label: property.communeName,
        link: `${process.env.NEXT_PUBLIC_LEGACY_HOST}/${process.env.NEXT_PUBLIC_COUNTRY}/terreno/venta/${commune}/${property.communeId}-${communeSlug}`
      });
    }

    return breadcrumbList;
  }, [city, citySlug, commune, communeSlug, property, region, regionSlug]);

  const handleOnSubmit = React.useCallback(() => {
    sendContact({
      projectId: property.id,
      projectCommuneId: property.communeId,
      city: property.communeName,
      province: property.cityName,
      operation: operationFormatted,
      region: property.regionName,
      category: translatedPropertyType,
      currency: property.principalCurrency,
      value: property.price
    });
  }, [
    sendContact,
    property.id,
    property.communeId,
    property.communeName,
    property.cityName,
    operationFormatted,
    property.regionName,
    translatedPropertyType,
    property.principalCurrency,
    property.price
  ]);

  const handleShowPhoneSubmit = React.useCallback(() => {
    sendShowPhoneContact({
      projectCommuneId: property.communeId,
      projectId: property.id,
      city: property.communeName,
      province: property.cityName,
      operation: operationFormatted,
      region: property.regionName,
      category: property.propertyType,
      currency: property.principalCurrency,
      value: property.price
    });
  }, [
    property.cityName,
    property.communeId,
    property.communeName,
    property.id,
    operationFormatted,
    property.price,
    property.principalCurrency,
    property.propertyType,
    property.regionName,
    sendShowPhoneContact
  ]);

  const handleOnClickWhatsappButton = React.useMemo(() => {
    if (!property.sellerWhatsapp) {
      return undefined;
    }

    return () =>
      sendWhatsappContact({
        projectCommuneId: property.communeId,
        projectId: property.id,
        city: property.communeName,
        province: property.cityName,
        operation: operationFormatted,
        region: property.regionName,
        category: property.propertyType,
        currency: property.principalCurrency,
        value: property.price
      });
  }, [
    property.sellerWhatsapp,
    sendWhatsappContact,
    property.communeId,
    property.id,
    property.communeName,
    property.cityName,
    operationFormatted,
    property.regionName,
    property.propertyType,
    property.principalCurrency,
    property.price
  ]);

  const BadgeContent = (
    <Badge
      operation={operation}
      campaignTextColor={property.campaignTextColor}
      campaignIconColor={property.campaignIconColor}
      propertyType={property.propertyType}
    />
  );

  return (
    <Fragment>
      <StructuredData
        currency={property.principalCurrency}
        image={property.photos[0].src}
        name={property.title}
        price={property.price}
        sku={property.id}
      />
      <PropertyPageHeader
        campaignChipText={property.campaignChipText}
        cityId={property.cityId}
        cityName={property.cityName}
        citySlug={citySlug}
        communeId={property.communeId}
        communeName={property.communeName}
        communeSlug={communeSlug}
        id={property.id}
        logo={property.propertyLogo}
        operation={operation}
        photos={property.photos}
        price={property.price}
        principalCurrency={property.principalCurrency}
        propertyType={t(property.propertyType)}
        regionName={property.regionName}
        title={property.title}
        videoId={property.videoId}
        videoProvider={property.videoProvider}
      />

      <Row>
        <Col
          xs={4}
          lg={8}
        >
          <Visible desktop>
            <Breadcrumb breadCrumbItems={breadCrumbItems} />
          </Visible>

          {property.projectCampaigns && (
            <Fragment>
              {property.projectCampaigns.map((campaign, key) => (
                <CampaignSubContent
                  key={key}
                  logo={campaign.logo}
                  name={campaign.card_title}
                  campaignDescription={property.campaignDescription}
                  campaignDiscountText={property.campaignDiscountText}
                />
              ))}
            </Fragment>
          )}

          {badges.length > 0 && <BadgesList badges={badges} />}

          <SubContent title='Características'>
            {BadgeContent}

            <Features
              priceLabel={t('projectPriceLabel')}
              totalSurfaceLabel={t('projectSurfaceLabel')}
            />
          </SubContent>

          <SubContent title='Descripción'>
            <StyledReadMore
              height={100}
              dangerously
            >
              {`${property.description} ${property.highlights}`}
            </StyledReadMore>
          </SubContent>

          {property.features.length > 0 && (
            <SubContent title='Servicios Básicos'>
              <FeatureList>
                {property.features.map((feature) => (
                  <Feature
                    key={feature.name}
                    icon={feature.icon}
                    name={feature.name}
                    value={feature.value}
                  />
                ))}
              </FeatureList>
            </SubContent>
          )}

          <SubContent title='Ubicación'>
            <LocationTabs />
          </SubContent>
        </Col>

        <Col
          xs={4}
          lg={4}
        >
          <StickyContainer>
            <ContactForm
              companyLogo={property.companyLogo}
              onSubmit={handleOnSubmit}
              propertyId={property.id}
              sellerName={property.sellerName}
              onClickWhatsappButton={handleOnClickWhatsappButton}
              onClickShowPhoneButton={handleShowPhoneSubmit}
              sellerPhone={property.sellerPhone}
              sellerWhatsapp={property.sellerWhatsapp}
              formHeader={
                <FormHeader
                  campaignPricing={property.campaignPricing}
                  secondaryCurrency={property.secondaryCurrency}
                  principalCurrency={property.principalCurrency}
                  pricing={property.pricing}
                  surface={property.surface}
                  surfaceUnit={property.surfaceUnit}
                />
              }
            >
              <FormValues
                propertyId={property.id}
                sellerWhatsapp={property.sellerWhatsapp}
                onClickWhatsappButton={handleOnClickWhatsappButton}
              />
            </ContactForm>
            <SellerInformation
              sellerName={property.sellerName}
              sellerLogo={property.companyLogo}
              sellerPhone={property.sellerPhone}
              onSubmit={handleShowPhoneSubmit}
            />
          </StickyContainer>
        </Col>
      </Row>

      {property.relatedProperties && property.relatedProperties.length > 0 && (
        <Row>
          <Col>
            <RelatedProperties
              epigraph={<PriceFromStyled>Desde</PriceFromStyled>}
              relatedProperties={property.relatedProperties}
              totalResultsPerPage={4}
              totalPages={5}
              totalResults={20}
              title={t('property:relatedProjectsTitle')}
            />
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default ProjectPage;
